import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { JeptoUiProgressBarIndeterminateComponent } from '../../../../ui-lib/buttons-indicators/jepto-ui-progress-bar-indeterminate/jepto-ui-progress-bar-indeterminate.component';
import { ImageCardConfig } from '../../../models/card-model';
import { DataService } from '@framework/analytics/services/data.service';
import { displayableImageRef } from '../../../utils/card-image-ref.util';

/**
 * Image Card Component
 *
 * Renders an image inside a card container. Unlike chart, HTML, and markdown cards,
 * the image card uses **only** card config to render—no WebSocket or DataService.
 * Config (including imageRef, imageRefLight, imageRefDark) is read from CardService
 * when in connected mode, or from direct inputs in standalone mode.
 */
@Component({
	selector: 'jepto-image-card',
	templateUrl: './jepto-image-card.component.html',
	styleUrls: ['./jepto-image-card.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatIconModule,
		MatMenuModule,
		JeptoUiProgressBarIndeterminateComponent,
	],
})
export class JeptoImageCardComponent extends JeptoBaseCardComponent {
	/** Default/fallback image URL or S3 reference */
	imageRef = input<string>('');
	/** Image URL or S3 reference for light theme */
	imageRefLight = input<string>();
	/** Image URL or S3 reference for dark theme */
	imageRefDark = input<string>();
	/** When true, shows loading indicator */
	loading = input<boolean>(false);
	/** Accessible alt text for the image */
	alt = input<string>('');
	/** Max image height in px. Omit or null = no constraint. */
	maxHeight = input<number | null | undefined>(undefined);
	/** Max image width in px. Omit or null = auto (no constraint). */
	maxWidth = input<number | null | undefined>(undefined);

	/** Test ID for component targeting in tests (defaults to cardId for uniqueness) */
	testId = input<string>();

	private dataService = inject(DataService, { optional: true });

	/** Computed testId that defaults to cardId when available */
	protected effectiveTestId = computed(
		() =>
			this.testId() ??
			(this.baseConfig() as ImageCardConfig | undefined)?.cardId ??
			this.cardId() ??
			'image-card'
	);

	/**
	 * Effective refs: WebSocket data (DataService) takes priority, then direct
	 * input, then card config from CardService.
	 */
	protected effectiveImageRef = computed(() => {
		const id = this.cardId();
		if (id && this.dataService) {
			const fromService = this.dataService.imageDataSignal(id)();
			if (fromService?.imageRef !== undefined) {
				const displayable = displayableImageRef(fromService.imageRef);
				if (displayable) {
					return displayable;
				}
			}
		}
		const direct = displayableImageRef(this.imageRef());
		if (direct) return direct;
		const config = this.baseConfig() as ImageCardConfig | undefined;
		return displayableImageRef(config?.imageRef ?? '');
	});

	protected effectiveImageRefLight = computed(() => {
		const id = this.cardId();
		if (id && this.dataService) {
			const fromService = this.dataService.imageDataSignal(id)();
			if (fromService?.imageRefLight !== undefined) {
				const displayable = displayableImageRef(
					fromService.imageRefLight
				);
				if (displayable) {
					return displayable;
				}
			}
		}
		const direct = displayableImageRef(this.imageRefLight());
		if (direct) return direct;
		const config = this.baseConfig() as ImageCardConfig | undefined;
		return displayableImageRef(config?.imageRefLight ?? '');
	});

	protected effectiveImageRefDark = computed(() => {
		const id = this.cardId();
		if (id && this.dataService) {
			const fromService = this.dataService.imageDataSignal(id)();
			if (fromService?.imageRefDark !== undefined) {
				const displayable = displayableImageRef(
					fromService.imageRefDark
				);
				if (displayable) {
					return displayable;
				}
			}
		}
		const direct = displayableImageRef(this.imageRefDark());
		if (direct) return direct;
		const config = this.baseConfig() as ImageCardConfig | undefined;
		return displayableImageRef(config?.imageRefDark ?? '');
	});

	/** Resolved image URL for the active theme (light/dark). Uses refs as URLs; S3 resolution is app responsibility. */
	protected effectiveImageUrl = computed(() => {
		const mode = this.activeThemeMode();
		if (mode === 'dark') {
			const ref = this.effectiveImageRefDark();
			if (ref) return ref;
		}
		if (mode === 'light') {
			const ref = this.effectiveImageRefLight();
			if (ref) return ref;
		}
		return this.effectiveImageRef() || '';
	});

	protected effectiveAlt = computed(() => {
		const direct = this.alt();
		if (direct) return direct;
		return this.effectiveTitle() || '';
	});

	protected isLoading = computed(() => {
		return this.loading();
	});

	/** True when no image URL is available and not explicitly loading */
	protected hasNoImage = computed(() => {
		return !this.loading() && !this.effectiveImageUrl();
	});

	/** Effective max dimensions: direct input or from config. Undefined = auto (natural size). */
	protected effectiveMaxHeight = computed(() => {
		const direct = this.maxHeight();
		if (direct !== undefined) return direct;
		const config = this.baseConfig() as ImageCardConfig | undefined;
		return config?.maxHeight;
	});

	protected effectiveMaxWidth = computed(() => {
		const direct = this.maxWidth();
		if (direct !== undefined) return direct;
		const config = this.baseConfig() as ImageCardConfig | undefined;
		return config?.maxWidth;
	});

	/** Inline styles for img when max dimensions are set */
	protected imageStyles = computed(() => {
		const styles: Record<string, string> = {};
		const h = this.effectiveMaxHeight();
		const w = this.effectiveMaxWidth();
		if (h != null) styles['max-height'] = `${h}px`;
		if (w != null) styles['max-width'] = `${w}px`;
		return styles;
	});
}
